// src/registered/site-admin/meter_catalogue/MeterCatalogueValidator.js
//
// Pure validators — validate(input, ctx) -> { isValid, errors }. Same shape as
// the other SUSTINA site-admin validators.

import { CONCEPT_KEY_RE, IDENTIFIER_SCHEMES, parseObis } from "./MeterCatalogueDTO";

export const validateConcept = (concept = {}, existing = []) => {
  const errors = {};
  const key = (concept.conceptKey || "").trim();

  if (!key) {
    errors.conceptKey = "A canonical key is required (e.g. energy.active.import.total).";
  } else if (!CONCEPT_KEY_RE.test(key)) {
    errors.conceptKey =
      "Lowercase, dot-delimited, 2+ segments, [a-z0-9] only — no spaces or underscores.";
  } else if (
    existing.some(
      (c) =>
        c.meterConceptId !== concept.meterConceptId &&
        (c.conceptKey || "").trim().toLowerCase() === key.toLowerCase(),
    )
  ) {
    errors.conceptKey = "That concept key already exists.";
  }

  if (!(concept.label || "").trim()) errors.label = "A label is required.";
  if (!concept.commodityCode) errors.commodityCode = "Choose a commodity.";
  if (!concept.quantity) errors.quantity = "Choose a quantity.";
  if (!concept.canonicalUnitCode) errors.canonicalUnitCode = "Choose the canonical unit.";
  if (concept.statusCode === "deprecated" && !concept.replacedByMeterConceptId) {
    errors.replacedByMeterConceptId = "A deprecated concept must point at its replacement.";
  }

  return { isValid: Object.keys(errors).length === 0, errors };
};

export const validateIdentifier = (identifier = {}, siblings = []) => {
  const errors = {};
  const scheme = identifier.schemeCode;
  const display = (identifier.displayCode || "").trim();
  const ctx = (identifier.mappingContext || "abstract").trim();

  if (!IDENTIFIER_SCHEMES.some((s) => s.code === scheme)) {
    errors.schemeCode = "Choose a scheme.";
  }
  if (!display) {
    errors.displayCode = "Enter the identifier string.";
  } else if (scheme === "OBIS" && !parseObis(display)) {
    errors.displayCode = 'OBIS form is "A-B:C.D.E*F", e.g. 1-0:1.8.0';
  }
  if (
    siblings.some(
      (s) =>
        s.identifierId !== identifier.identifierId &&
        s.schemeCode === scheme &&
        (s.displayCode || "").trim() === display &&
        (s.mappingContext || "abstract").trim() === ctx,
    )
  ) {
    errors.displayCode = "This identifier + context is already mapped on this concept.";
  }
  if (identifier.mappingMethod === "AI_ASSISTED") {
    if (identifier.confidence == null || Number.isNaN(Number(identifier.confidence))) {
      errors.confidence = "AI-assisted mappings need a confidence (0–1).";
    }
    if (!["candidate", "deprecated"].includes(identifier.statusCode)) {
      errors.statusCode = "AI-assisted mappings stay 'candidate' until reviewed.";
    }
  }
  return { isValid: Object.keys(errors).length === 0, errors };
};

export const validateRegistration = (reg = {}, existing = []) => {
  const errors = {};
  const serial = (reg.meterSerial || "").trim();
  if (!serial) errors.meterSerial = "Meter serial is required.";
  else if (
    existing.some(
      (r) =>
        r.meterRegistrationId !== reg.meterRegistrationId &&
        (r.meterSerial || "").trim().toLowerCase() === serial.toLowerCase(),
    )
  ) {
    errors.meterSerial = "That meter serial is already registered.";
  }
  if (!reg.corporateEntityId) errors.corporateEntityId = "Choose the owning entity.";
  if (!reg.commodityCode) errors.commodityCode = "Choose a commodity.";
  return { isValid: Object.keys(errors).length === 0, errors };
};
